/**
 * This component is being used to render content from third party inside iframe
 *
 * @module views/components/ExternalContent
 * @memberof -Common
 */
import './ExternalContent.scss';

import React, { useCallback, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';

import Button from '@ulta/modules/Button/Button';

import * as utils from './ExternalContent';

/**
 * Represents a ExternalContent component
 *
 * @method
 * @param { ExternalContentProps} props - React properties passed from composition
 * @returns ExternalContent
 */
export const ExternalContent = function( props ){
  const { title, showMoreAction, showLessAction, contentUrl, action, actionIcon } = props;
  const [toggleDisplay, setToggleDisplay] = useState( false );
  const [scrollContent, setScrollContent] = useState( false );
  // to reset iframe once we click on show less to reload it from top
  const [resetIframe, setResetIframe] = useState( 0 );
  const scrollRef = useRef();

  const toggleContent = useCallback(
    utils.composeToggleCallback( { toggleDisplay, scrollContent, resetIframe },
      { setToggleDisplay, setScrollContent, setResetIframe } ),
    [toggleDisplay, scrollContent, resetIframe]
  );

  return (
    <div className='ExternalContent'>
      <div className={ classNames( 'ExternalContent__wrapper', {
        'ExternalContent--enableScroll' : scrollContent }
      ) }
      >
        <div className='ExternalContent__heading'>
          <div className='ExternalContent__heading--title'>
            <Text
              htmlTag='h2'
              textStyle='title-6'
            >{ title }</Text>
          </div>
          {
            action?.url &&
            <div className='ExternalContent__heading--printLink'>
              <Link_Huge
                likeButtonSecondary
                likeButtonCompact
                iconImage={ actionIcon }
                iconRight
                iconsize='s'
                action={ action }
              >
                { action?.label }
              </Link_Huge>
            </div>
          }
        </div>
        <div
          id='iFrameContainer'
          className={ classNames( 'ExternalContent__iframeContainer', {
            'ExternalContent--dynamicHeight' : scrollContent }
          ) }
        >

          <iframe
            key={ resetIframe }
            ref={ scrollRef }
            title={ title }
            src={ contentUrl }
          />
        </div>
        <div className='ExternalContent__toggleCTA'>
          <Button
            isExpanded={ toggleDisplay }
            label={ !toggleDisplay ? showMoreAction?.label : showLessAction?.label }
            likeLink
            onClick={ toggleContent }
            ariaControls='iFrameContainer'
          >
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
* composeToggleCallback - is used to toggle iframe content
* @param {object} data - Argument
* @param {boolean} data.toggleDisplay - to show or hide button
* @param {boolean} data.scrollContent - to check if component is scrolling
* @param {object} methods - object contains methods which are passed as args to the function
* @param {function} methods.setToggleDisplay - to set button label and scrolling
* @param {function} methods.setScrollContent - to set iframe content height
* @param {function} methods.setResetIframe - sets iframe reloading
**/

export const composeToggleCallback = ( data, methods )=>()=>{
  const { setToggleDisplay, setScrollContent, setResetIframe } = methods;
  const { toggleDisplay, scrollContent, resetIframe } = data;
  setToggleDisplay( !toggleDisplay );
  setScrollContent( !scrollContent );
  setResetIframe( resetIframe + 1 );

};

/**
 * Property type definitions
 * @typedef ExternalContentProps
 * @type {object}
 * @property {string} title - displays External content title
 * @property {object} action - Sets action for print button
 * @property {object} showMoreAction - Sets action for showmore button
 * @property {object} showLessAction - Sets action for show less button
 * @property {string} contentUrl - Sets the content url for disclaimer iframe
 * @property {string} actionIcon - Sets the print icon
 */
export const propTypes = {
  title: PropTypes.string,
  action: PropTypes.object,
  showMoreAction: PropTypes.object,
  showLessAction: PropTypes.object,
  contentUrl: PropTypes.string,
  actionIcon: PropTypes.string
};

ExternalContent.propTypes = propTypes;

export default ExternalContent;
